import React, { createContext, useContext, useState, useMemo, Dispatch, SetStateAction, ReactNode } from 'react';
import { AddAttendanceListType, AttendanceInRangeType, AttendanceThresholdType, StudentAttendanceInRange } from '../types';

interface AttendanceContextType {
  selectedFilter: string;
  onFilterChange: (value: string) => void;
  filteredAttendanceInRange: AttendanceInRangeType;
  setFilteredAttendanceInRange: Dispatch<SetStateAction<AttendanceInRangeType>>;
  filteredStudentAttendanceInRange: StudentAttendanceInRange;
  setFilteredStudentAttendanceInRange: Dispatch<SetStateAction<StudentAttendanceInRange>>;
  setFilterClassForAttendance: Dispatch<SetStateAction<AddAttendanceListType>>;
  filterClassForAttendance: AddAttendanceListType;
  setFilteredThresholdData: Dispatch<SetStateAction<AttendanceThresholdType[]>>;
  filteredThresholdData: AttendanceThresholdType[];
  setAddAttendanceLoading: Dispatch<SetStateAction<boolean>>;
  addAttendanceLoading: boolean;
  setViewAttendanceLoading: Dispatch<SetStateAction<boolean>>;
  viewAttendanceLoading: boolean;
  setAttendanceThresholdLoading: Dispatch<SetStateAction<boolean>>;
  attendanceThresholdLoading: boolean;
  setStudentAttendanceListLoading: Dispatch<SetStateAction<boolean>>;
  studentAttendanceListLoading: boolean;
}

const AttendanceContext = createContext<AttendanceContextType | null>(null);

const DEFAULT_SELECTED_FILTER = 'View Attendance';

const AttendanceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedFilter, setSelectedFilter] = useState(DEFAULT_SELECTED_FILTER);
  const [filteredAttendanceInRange, setFilteredAttendanceInRange] = useState<AttendanceInRangeType>({});
  const [filteredStudentAttendanceInRange, setFilteredStudentAttendanceInRange] = useState<StudentAttendanceInRange>({} as StudentAttendanceInRange);
  const [filterClassForAttendance, setFilterClassForAttendance] = useState<AddAttendanceListType>({} as AddAttendanceListType);
  const [filteredThresholdData, setFilteredThresholdData] = useState<AttendanceThresholdType[]>([]);
  const [addAttendanceLoading, setAddAttendanceLoading] = useState(false);
  const [viewAttendanceLoading, setViewAttendanceLoading] = useState(false);
  const [attendanceThresholdLoading, setAttendanceThresholdLoading] = useState(false);
  const [studentAttendanceListLoading, setStudentAttendanceListLoading] = useState(false);

  const onFilterChange = (value: string) => {
    setFilterClassForAttendance({} as AddAttendanceListType);
    setSelectedFilter(value);
  };

  const contextValue = useMemo(
    () => ({
      onFilterChange,
      selectedFilter,
      filteredAttendanceInRange,
      setFilteredAttendanceInRange,
      filteredStudentAttendanceInRange,
      setFilteredStudentAttendanceInRange,
      filterClassForAttendance,
      setFilterClassForAttendance,
      filteredThresholdData,
      setFilteredThresholdData,
      addAttendanceLoading,
      setAddAttendanceLoading,
      viewAttendanceLoading,
      setViewAttendanceLoading,
      attendanceThresholdLoading,
      setAttendanceThresholdLoading,
      studentAttendanceListLoading,
      setStudentAttendanceListLoading,
    }),
    [
      selectedFilter,
      filteredAttendanceInRange,
      filteredStudentAttendanceInRange,
      filterClassForAttendance,
      filteredThresholdData,
      addAttendanceLoading,
      viewAttendanceLoading,
      attendanceThresholdLoading,
      studentAttendanceListLoading,
    ]
  );

  return <AttendanceContext.Provider value={contextValue}>{children}</AttendanceContext.Provider>;
};

export default AttendanceProvider;

export const useAttendance = (): AttendanceContextType => {
  const context = useContext(AttendanceContext);
  if (!context) {
    throw new Error('useAttendance must be used within an AttendanceProvider');
  }
  return context;
};
